import { AxiosError } from 'axios';

import type { ApiError, BackendErrorResponse } from './apiError.types';

/**
 * Normalizes errors from different sources into a consistent ApiError format
 * Handles:
 * - Backend structured error responses
 * - Network errors
 * - Unexpected errors
 *
 * @param error - Error to normalize (can be AxiosError, Error, or unknown)
 * @returns Normalized ApiError object
 */
export function normalizeError(error: unknown): ApiError {
  // Handle AxiosError with backend error response
  if (error instanceof AxiosError && error.response?.data) {
    const backendError = error.response.data as BackendErrorResponse;

    // Check if response matches backend error structure
    if (backendError.errors && Array.isArray(backendError.errors)) {
      return {
        status: backendError.status || error.response.status,
        errors: backendError.errors.map((err) => ({
          message: err.message,
          labelKey: err.labelKey,
          field: err.field,
          params: err.params,
        })),
        traceId: backendError.traceId,
        originalError: error,
      };
    }
  }

  // Handle AxiosError without structured response
  if (error instanceof AxiosError) {
    return {
      status: error.response?.status || 500,
      errors: [
        {
          message: error.message || 'An unexpected error occurred',
        },
      ],
      originalError: error,
    };
  }

  // Handle generic errors
  if (error instanceof Error) {
    return {
      status: 500,
      errors: [
        {
          message: error.message || 'An unexpected error occurred',
        },
      ],
      originalError: error,
    };
  }

  // Handle unknown errors
  return {
    status: 500,
    errors: [
      {
        message: 'An unexpected error occurred',
      },
    ],
    originalError: error,
  };
}
